import { useSearchParams } from "react-router-dom";

import {
  LIST_PARAM,
  VACANCY_FILTER_KEYS,
  VACANCY_LIST,
  type VacancyFilterKey,
  type VacancyListKind,
} from "../constants";
import {
  withoutVacancyFilter,
  withoutVacancyFilters,
} from "../vacancyFilterParams";

import { URL_PARAMS } from "@/constants/urlParams";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";

const parseOption = <T extends string>(
  options: Record<string, T>,
  value: string | null | undefined,
  fallback: T,
): T => Object.values(options).find((option) => option === value) ?? fallback;

export function useVacancyListParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = useSortSearchParams();

  const list: VacancyListKind = parseOption(
    VACANCY_LIST,
    searchParams.get(LIST_PARAM),
    VACANCY_LIST.INCLUDED,
  );
  const descending = sort.descending ?? false;

  const filters = Object.fromEntries(
    VACANCY_FILTER_KEYS.map((key) => [key, searchParams.getAll(key)]),
  ) as Record<VacancyFilterKey, string[]>;
  const activeFilterCount = Object.values(filters).reduce(
    (total, values) => total + values.length,
    0,
  );

  const update = (apply: (next: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        apply(next);
        return next;
      },
      { replace: true },
    );
  const replaceParams = (build: (prev: URLSearchParams) => URLSearchParams) =>
    setSearchParams(build, { replace: true });

  return {
    list,
    descending,
    filters,
    activeFilterCount,
    setList: (value: VacancyListKind) =>
      update((next) => next.set(LIST_PARAM, value)),
    setDescending: (value: boolean) =>
      update((next) => next.set(URL_PARAMS.DESCENDING, String(value))),
    removeFilter: (key: string, value: string) =>
      replaceParams((prev) => withoutVacancyFilter(prev, key, value)),
    clearFilters: () => replaceParams(withoutVacancyFilters),
  };
}
